import { EmptyState, LoadingSkeleton } from "@/components/auditoria/estados";
import { LinhaDoTempo } from "@/components/auditoria/LinhaDoTempo";
import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import {
  useEmpresas,
  useFaturas,
  useComprasHistorico,
  useComprasHistoricoOwner,
  useCreditosTransacoes,
  type CreditoTransacao,
} from "@/hooks/use-data";
import type { Fatura } from "@shared/schema";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  ShoppingCart,
  ArrowDownUp,
  Receipt,
  ArrowRightLeft,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  Clock,
  XCircle,
} from "lucide-react";
import { useState, useMemo } from "react";
import { cn, formatCurrency } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { supabase } from "@/lib/supabase";
import {
  fmtDateTime,
  metodoPgtoLabel,
  tipoVeiculoLabel,
  fmtDate,
} from "@/lib/formatters";

// ─── constants ─────────────────────────────────────────────────────────────────

const TIPO_TRANSACAO_INFO: Record<
  string,
  { label: string; cor: string; entrada: boolean }
> = {
  compra: { label: "Compra", cor: "text-emerald-600", entrada: true },
  liberacao: { label: "Liberação", cor: "text-emerald-600", entrada: true },
  estorno_credito: {
    label: "Estorno (crédito)",
    cor: "text-emerald-600",
    entrada: true,
  },
  reserva: { label: "Reserva", cor: "text-amber-600", entrada: false },
  debito: { label: "Débito", cor: "text-red-600", entrada: false },
  estorno_dinheiro: {
    label: "Estorno (dinheiro)",
    cor: "text-slate-500",
    entrada: false,
  },
  gratuito: { label: "Gratuito", cor: "text-blue-500", entrada: true },
};

const FATURA_STATUS_INFO: Record<
  string,
  { label: string; cor: string; bg: string }
> = {
  pendente: {
    label: "Pendente",
    cor: "text-amber-700",
    bg: "bg-amber-50 dark:bg-amber-900/20",
  },
  pago: {
    label: "Paga",
    cor: "text-emerald-700",
    bg: "bg-emerald-50 dark:bg-emerald-900/20",
  },
  atrasada: {
    label: "Atrasada",
    cor: "text-red-700",
    bg: "bg-red-50 dark:bg-red-900/20",
  },
  bloqueada: {
    label: "Bloqueada",
    cor: "text-red-800",
    bg: "bg-red-100 dark:bg-red-900/30",
  },
  vencido: {
    label: "Vencida",
    cor: "text-orange-700",
    bg: "bg-orange-50 dark:bg-orange-900/20",
  },
  cancelado: {
    label: "Cancelada",
    cor: "text-slate-500",
    bg: "bg-slate-100 dark:bg-slate-800",
  },
};

const COMPRA_STATUS_INFO: Record<
  string,
  { label: string; cor: string; bg: string }
> = {
  pendente: {
    label: "Pendente",
    cor: "text-amber-700",
    bg: "bg-amber-50 dark:bg-amber-900/20",
  },
  aprovado: {
    label: "Aprovado",
    cor: "text-emerald-700",
    bg: "bg-emerald-50 dark:bg-emerald-900/20",
  },
  pago: {
    label: "Pago",
    cor: "text-emerald-700",
    bg: "bg-emerald-50 dark:bg-emerald-900/20",
  },
  cancelado: {
    label: "Cancelado",
    cor: "text-slate-500",
    bg: "bg-slate-100 dark:bg-slate-800",
  },
  expirado: {
    label: "Expirado",
    cor: "text-red-600",
    bg: "bg-red-50 dark:bg-red-900/20",
  },
};

const SPLIT_STATUS_INFO: Record<
  string,
  { label: string; cor: string; icon: typeof CheckCircle2 }
> = {
  concluida: {
    label: "Concluída",
    cor: "text-emerald-600",
    icon: CheckCircle2,
  },
  processando: { label: "Processando", cor: "text-amber-600", icon: Clock },
  falhou: { label: "Falhou", cor: "text-red-600", icon: XCircle },
  pendente: { label: "Pendente", cor: "text-slate-500", icon: Clock },
};

const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

// ─── EmptyState ────────────────────────────────────────────────────────────────

// ─── Tab: Compras ──────────────────────────────────────────────────────────────

function TabCompras({
  isOwner,
  empresaId,
}: {
  isOwner: boolean;
  empresaId: string;
}) {
  const ownerQuery = useComprasHistoricoOwner();
  const adminQuery = useComprasHistorico(isOwner ? undefined : empresaId);
  const { data: compras, isLoading } = isOwner ? ownerQuery : adminQuery;

  const [filtroStatus, setFiltroStatus] = useState("");

  const lista = useMemo(() => {
    const all = (compras as any[]) ?? [];
    return filtroStatus ? all.filter((c) => c.status === filtroStatus) : all;
  }, [compras, filtroStatus]);

  const totalValor = lista.reduce((s, c) => s + (c.valor_total ?? 0), 0);
  const pagas = lista.filter(
    (c) => c.status === "aprovado" || c.status === "pago",
  ).length;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-slate-500 mb-1">Total de compras</p>
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {lista.length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-slate-500 mb-1">Pagas</p>
            <p className="text-2xl font-bold text-emerald-600">{pagas}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-slate-500 mb-1">Volume total</p>
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {formatCurrency(totalValor)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Label className="text-xs text-slate-500 shrink-0">Status:</Label>
        <Select
          value={filtroStatus}
          onValueChange={(v) => setFiltroStatus(v === "__all__" ? "" : v)}
        >
          <SelectTrigger className="h-8 w-40">
            <SelectValue placeholder="Todos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos</SelectItem>
            {Object.entries(COMPRA_STATUS_INFO).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingSkeleton />
          ) : lista.length === 0 ? (
            <EmptyState
              icon={ShoppingCart}
              message="Nenhuma compra encontrada"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    {[
                      "Data",
                      "Parceiro",
                      isOwner ? "Vistoriadora" : null,
                      "Valor",
                      "Laudos",
                      "Método",
                      "Status",
                    ]
                      .filter(Boolean)
                      .map((h, i) => (
                        <th
                          key={i}
                          className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {lista.map((c: any) => {
                    const si = COMPRA_STATUS_INFO[c.status] ?? {
                      label: c.status,
                      cor: "text-slate-500",
                      bg: "bg-slate-100",
                    };
                    return (
                      <tr
                        key={c.id}
                        className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                          {fmtDateTime(c.criado_em)}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-200">
                          {c.user_nome ?? "—"}
                        </td>
                        {isOwner && (
                          <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-[160px] truncate">
                            {c.vistoriadora_nome ?? c.empresa_nome ?? "—"}
                          </td>
                        )}
                        <td className="px-4 py-3 text-sm font-semibold text-slate-800 dark:text-slate-200">
                          {formatCurrency(c.valor_total ?? 0)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                          {c.quantidade ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                          {metodoPgtoLabel(c.metodo_pagamento)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              "text-xs font-medium px-2 py-0.5 rounded",
                              si.bg,
                              si.cor,
                            )}
                          >
                            {si.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Tab: Transações ───────────────────────────────────────────────────────────

interface TransacaoAll extends CreditoTransacao {
  empresa_id?: string;
}

function useTransacoesAll(isOwner: boolean, empresaIdFiltro?: string) {
  return useQuery<TransacaoAll[]>({
    queryKey: ["credito_transacoes_all", isOwner, empresaIdFiltro],
    queryFn: async () => {
      let query = supabase
        .from("credito_transacoes")
        .select(
          "id, tipo, quantidade, criado_em, compra_id, laudo_id, tipo_veiculo, empresa_id",
        )
        .order("criado_em", { ascending: false })
        .limit(200);
      if (empresaIdFiltro) query = query.eq("empresa_id", empresaIdFiltro);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as TransacaoAll[];
    },
    enabled: isOwner,
  });
}

function TabTransacoes({
  isOwner,
  empresaId,
  empresas,
}: {
  isOwner: boolean;
  empresaId: string;
  empresas: { id: string; razaoSocial: string }[];
}) {
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEmpresa, setFiltroEmpresa] = useState("");

  const ownerQuery = useTransacoesAll(isOwner, filtroEmpresa || undefined);
  const adminQuery = useCreditosTransacoes(isOwner ? undefined : empresaId);
  const { data: transacoes, isLoading } = isOwner ? ownerQuery : adminQuery;

  const lista = useMemo(() => {
    const all = transacoes ?? [];
    return filtroTipo ? all.filter((t) => t.tipo === filtroTipo) : all;
  }, [transacoes, filtroTipo]);

  const entradas = lista
    .filter((t) => TIPO_TRANSACAO_INFO[t.tipo]?.entrada)
    .reduce((s, t) => s + t.quantidade, 0);
  const saidas = lista
    .filter((t) => !TIPO_TRANSACAO_INFO[t.tipo]?.entrada)
    .reduce((s, t) => s + t.quantidade, 0);

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-slate-500 mb-1">Total movimentos</p>
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {lista.length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 flex items-start gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-500 mt-1 shrink-0" />
            <div>
              <p className="text-xs text-slate-500 mb-1">Entradas</p>
              <p className="text-2xl font-bold text-emerald-600">+{entradas}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 flex items-start gap-2">
            <TrendingDown className="w-4 h-4 text-red-500 mt-1 shrink-0" />
            <div>
              <p className="text-xs text-slate-500 mb-1">Saídas</p>
              <p className="text-2xl font-bold text-red-600">-{saidas}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500 shrink-0">Tipo:</Label>
          <Select
            value={filtroTipo}
            onValueChange={(v) => setFiltroTipo(v === "__all__" ? "" : v)}
          >
            <SelectTrigger className="h-8 w-44">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {Object.entries(TIPO_TRANSACAO_INFO).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isOwner && (
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500 shrink-0">Empresa:</Label>
            <Select
              value={filtroEmpresa}
              onValueChange={(v) => setFiltroEmpresa(v === "__all__" ? "" : v)}
            >
              <SelectTrigger className="h-8 w-52">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas</SelectItem>
                {empresas.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.razaoSocial}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingSkeleton />
          ) : lista.length === 0 ? (
            <EmptyState
              icon={ArrowDownUp}
              message="Nenhuma transação encontrada"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    {[
                      "Data",
                      "Tipo",
                      "Quantidade",
                      "Tipo Veículo",
                      "Laudo ID",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lista.map((t) => {
                    const info = TIPO_TRANSACAO_INFO[t.tipo] ?? {
                      label: t.tipo,
                      cor: "text-slate-600",
                      entrada: false,
                    };
                    return (
                      <tr
                        key={t.id}
                        className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                          {fmtDateTime(t.criado_em)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              "text-sm font-medium flex items-center gap-1",
                              info.cor,
                            )}
                          >
                            {info.entrada ? (
                              <TrendingUp className="w-3.5 h-3.5" />
                            ) : (
                              <TrendingDown className="w-3.5 h-3.5" />
                            )}
                            {info.label}
                          </span>
                        </td>
                        <td
                          className={cn(
                            "px-4 py-3 text-sm font-semibold",
                            info.cor,
                          )}
                        >
                          {info.entrada ? "+" : "-"}
                          {t.quantidade}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                          {t.tipo_veiculo
                            ? tipoVeiculoLabel(t.tipo_veiculo)
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400 font-mono text-xs">
                          {t.laudo_id ? t.laudo_id.slice(0, 8) + "…" : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Tab: Faturas ──────────────────────────────────────────────────────────────

function TabFaturas({
  isOwner,
  empresaId,
  empresas,
}: {
  isOwner: boolean;
  empresaId: string;
  empresas: { id: string; razaoSocial: string }[];
}) {
  const now = new Date();
  const [filtroVistoriadora, setFiltroVistoriadora] = useState(
    isOwner ? "" : empresaId,
  );
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroMes, setFiltroMes] = useState<number | undefined>();
  const [filtroAno, setFiltroAno] = useState<number | undefined>(
    now.getFullYear(),
  );

  const { data: faturas, isLoading } = useFaturas(
    filtroVistoriadora || undefined,
    filtroMes,
    filtroAno,
    filtroStatus || undefined,
  );

  const lista = faturas ?? [];
  const totalPendente = lista
    .filter((f) => f.status !== "pago" && f.status !== "cancelado")
    .reduce((s, f) => s + parseFloat(String(f.valorTotal ?? 0)), 0);
  const totalPago = lista
    .filter((f) => f.status === "pago")
    .reduce((s, f) => s + parseFloat(String(f.valorTotal ?? 0)), 0);

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-slate-500 mb-1">Total faturas</p>
            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {lista.length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-slate-500 mb-1">Em aberto</p>
            <p className="text-2xl font-bold text-amber-600">
              {formatCurrency(totalPendente)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-slate-500 mb-1">Pagas</p>
            <p className="text-2xl font-bold text-emerald-600">
              {formatCurrency(totalPago)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        {isOwner && (
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500 shrink-0">
              Vistoriadora:
            </Label>
            <Select
              value={filtroVistoriadora}
              onValueChange={(v) =>
                setFiltroVistoriadora(v === "__all__" ? "" : v)
              }
            >
              <SelectTrigger className="h-8 w-52">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas</SelectItem>
                {empresas.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.razaoSocial}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500 shrink-0">Status:</Label>
          <Select
            value={filtroStatus}
            onValueChange={(v) => setFiltroStatus(v === "__all__" ? "" : v)}
          >
            <SelectTrigger className="h-8 w-40">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {Object.entries(FATURA_STATUS_INFO).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500 shrink-0">Mês:</Label>
          <Select
            value={filtroMes ? String(filtroMes) : "__all__"}
            onValueChange={(v) =>
              setFiltroMes(v === "__all__" ? undefined : Number(v))
            }
          >
            <SelectTrigger className="h-8 w-36">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {MESES.map((m, i) => (
                <SelectItem key={i + 1} value={String(i + 1)}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500 shrink-0">Ano:</Label>
          <Select
            value={filtroAno ? String(filtroAno) : "__all__"}
            onValueChange={(v) =>
              setFiltroAno(v === "__all__" ? undefined : Number(v))
            }
          >
            <SelectTrigger className="h-8 w-28">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {[
                now.getFullYear(),
                now.getFullYear() - 1,
                now.getFullYear() - 2,
              ].map((a) => (
                <SelectItem key={a} value={String(a)}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingSkeleton />
          ) : lista.length === 0 ? (
            <EmptyState icon={Receipt} message="Nenhuma fatura encontrada" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    {[
                      "Nº",
                      "Período",
                      "Vencimento",
                      "Valor Base",
                      "Encargos",
                      "Total",
                      "Status",
                      "Pago em",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lista.map((f: Fatura) => {
                    const si = FATURA_STATUS_INFO[f.status] ?? {
                      label: f.status,
                      cor: "text-slate-500",
                      bg: "bg-slate-100",
                    };
                    const multa = parseFloat(String(f.multa ?? 0));
                    const juros = parseFloat(String(f.juros ?? 0));
                    const base =
                      parseFloat(String(f.valorTotal ?? 0)) - multa - juros;
                    return (
                      <tr
                        key={f.id}
                        className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">
                          #{f.numero}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                          {MESES[(f.mes ?? 1) - 1]}/{f.ano}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                          {fmtDate(f.vencimento)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                          {formatCurrency(base)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {multa + juros > 0 ? (
                            <span className="text-orange-600 font-medium">
                              {formatCurrency(multa + juros)}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-slate-800 dark:text-slate-100">
                          {formatCurrency(
                            parseFloat(String(f.valorTotal ?? 0)),
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              "text-xs font-medium px-2 py-0.5 rounded",
                              si.bg,
                              si.cor,
                            )}
                          >
                            {si.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {f.pagoEm ? fmtDate(String(f.pagoEm)) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Tab: Splits ───────────────────────────────────────────────────────────────

type SplitTransferencia = {
  id: string;
  compraId: string;
  vistoriadoraId: string;
  valorTotal: string;
  valorVistoriadora: string;
  valorOwner: string;
  percentual: string;
  asaasTransferId: string | null;
  status: string;
  criadoEm: string;
  vistoriadora: { razaoSocial: string; nomeFantasia: string | null } | null;
  compra: {
    empresaId: string;
    parceiro: { razaoSocial: string } | null;
  } | null;
};

type SplitResponse = {
  transferencias: SplitTransferencia[];
  totais: {
    quantidade: number;
    concluidas: number;
    totalGeral: string;
    totalTransferido: string;
    totalOwner: string;
  };
};

function TabSplits({ session }: { session: { access_token: string } | null }) {
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [ano, setAno] = useState(now.getFullYear());

  const dataInicio = new Date(ano, mes - 1, 1).toISOString();
  const dataFim = new Date(ano, mes, 1).toISOString();

  const { data, isLoading } = useQuery<SplitResponse>({
    queryKey: ["/api/split-transferencias/extrato", dataInicio, dataFim],
    queryFn: async () => {
      const params = new URLSearchParams({ dataInicio, dataFim });
      const res = await apiRequest(
        "GET",
        `/api/split-transferencias/extrato?${params}`,
      );
      return res.json();
    },
    enabled: !!session,
  });

  const transferencias = data?.transferencias ?? [];
  const totais = data?.totais;

  function navMes(delta: number) {
    let nm = mes + delta;
    let na = ano;
    if (nm > 12) {
      nm = 1;
      na++;
    }
    if (nm < 1) {
      nm = 12;
      na--;
    }
    setMes(nm);
    setAno(na);
  }

  return (
    <div className="space-y-4">
      {/* Período */}
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => navMes(-1)}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300 min-w-[130px] text-center">
          {MESES[mes - 1]} / {ano}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => navMes(1)}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* KPIs */}
      {totais && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-slate-500 mb-1">Transferências</p>
              <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                {totais.quantidade}
              </p>
              <p className="text-xs text-slate-400">
                {totais.concluidas} concluídas
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-slate-500 mb-1">Volume total</p>
              <p className="text-xl font-bold text-slate-800 dark:text-slate-100">
                {formatCurrency(parseFloat(totais.totalGeral ?? "0"))}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-slate-500 mb-1">Para vistoriadoras</p>
              <p className="text-xl font-bold text-blue-600">
                {formatCurrency(parseFloat(totais.totalTransferido ?? "0"))}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-slate-500 mb-1">Receita plataforma</p>
              <p className="text-xl font-bold text-emerald-600">
                {formatCurrency(parseFloat(totais.totalOwner ?? "0"))}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingSkeleton />
          ) : transferencias.length === 0 ? (
            <EmptyState
              icon={ArrowRightLeft}
              message="Nenhum split encontrado neste período"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    {[
                      "Data",
                      "Vistoriadora",
                      "Parceiro",
                      "Total",
                      "Vistoriadora",
                      "Plataforma",
                      "%",
                      "Status",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transferencias.map((t) => {
                    const si = SPLIT_STATUS_INFO[t.status] ?? {
                      label: t.status,
                      cor: "text-slate-500",
                      icon: Clock,
                    };
                    const Icon = si.icon;
                    return (
                      <tr
                        key={t.id}
                        className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                          {fmtDateTime(t.criadoEm)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300 max-w-[150px] truncate">
                          {t.vistoriadora?.nomeFantasia ||
                            t.vistoriadora?.razaoSocial ||
                            "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-[150px] truncate">
                          {t.compra?.parceiro?.razaoSocial ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-slate-800 dark:text-slate-200">
                          {formatCurrency(parseFloat(t.valorTotal))}
                        </td>
                        <td className="px-4 py-3 text-sm text-blue-600 font-medium">
                          {formatCurrency(parseFloat(t.valorVistoriadora))}
                        </td>
                        <td className="px-4 py-3 text-sm text-emerald-600 font-medium">
                          {formatCurrency(parseFloat(t.valorOwner))}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {parseFloat(t.percentual).toFixed(0)}%
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              "text-sm font-medium flex items-center gap-1",
                              si.cor,
                            )}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            {si.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export function Auditoria() {
  const { profile, session } = useAuth();
  const roles = profile?.roles ?? [];
  const isOwner = roles.includes("owner");
  const isAdmin = roles.includes("admin");
  const empresaId = profile?.empresaId ?? "";

  const { data: empresasData } = useEmpresas();
  const empresas = (empresasData ?? []).map((e) => ({
    id: e.id,
    razaoSocial: e.razaoSocial,
  }));

  return (
    <Shell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              Auditoria
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {isOwner
                ? "Tudo o que acontece no sistema, de todas as empresas"
                : "Tudo o que acontece na sua empresa"}
            </p>
          </div>
        </div>

        <Tabs defaultValue="logs">
          <TabsList className="mb-2">
            <TabsTrigger value="logs" className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Logs
            </TabsTrigger>
            <TabsTrigger value="compras" className="flex items-center gap-1.5">
              <ShoppingCart className="w-3.5 h-3.5" /> Compras
            </TabsTrigger>
            <TabsTrigger
              value="transacoes"
              className="flex items-center gap-1.5"
            >
              <ArrowDownUp className="w-3.5 h-3.5" /> Créditos/Débitos
            </TabsTrigger>
            <TabsTrigger value="faturas" className="flex items-center gap-1.5">
              <Receipt className="w-3.5 h-3.5" /> Faturas
            </TabsTrigger>
            {isOwner && (
              <TabsTrigger value="splits" className="flex items-center gap-1.5">
                <ArrowRightLeft className="w-3.5 h-3.5" /> Splits
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="logs">
            <LinhaDoTempo isOwner={isOwner} empresas={empresas} />
          </TabsContent>

          <TabsContent value="compras">
            <TabCompras isOwner={isOwner} empresaId={empresaId} />
          </TabsContent>

          <TabsContent value="transacoes">
            <TabTransacoes
              isOwner={isOwner}
              empresaId={empresaId}
              empresas={empresas}
            />
          </TabsContent>

          <TabsContent value="faturas">
            <TabFaturas
              isOwner={isOwner}
              empresaId={empresaId}
              empresas={empresas}
            />
          </TabsContent>

          {isOwner && (
            <TabsContent value="splits">
              <TabSplits session={session} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Shell>
  );
}
